// Account list page — FE-038.

import { useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { AccountListFilters } from "./AccountListFilters";
import { AccountListTable } from "./AccountListTable";
import { useAccountListQuery } from "./hooks";
import {
  DEFAULT_LIMIT,
  parseFiltersFromSearch,
  parseOffsetFromSearch,
  parseSortFromSearch,
  serializeAccountListUrl,
} from "./urlState";
import type {
  AccountListFilters as Filters,
  AccountListSortKey,
} from "./types";


const PAGE_LIMIT = DEFAULT_LIMIT;


export function AccountListPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const filters = useMemo<Filters>(
    () => parseFiltersFromSearch(search),
    [search],
  );
  const { sortBy, sortDir } = useMemo(
    () => parseSortFromSearch(search),
    [search],
  );
  const offset = useMemo(
    () => parseOffsetFromSearch(search),
    [search],
  );
  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) =>
      v !== undefined && v !== null && v !== ""),
    [filters],
  );

  const updateUrl = useCallback(
    (
      nextFilters: Filters,
      nextSortBy: AccountListSortKey = sortBy,
      nextSortDir: "asc" | "desc" = sortDir,
      nextOffset: number = 0,
    ) => {
      const url = serializeAccountListUrl(
        nextFilters, nextSortBy, nextSortDir, nextOffset,
      );
      // ``navigate`` keeps the SPA's react-router state machine in
      // step with the URL — using ``setSearch`` alone doesn't honour
      // the path-only ``/dashboard/accounts`` shape we emit when no
      // params are set.
      navigate(url, { replace: false });
    },
    [navigate, sortBy, sortDir],
  );

  const query = useAccountListQuery({
    filters,
    sortBy, sortDir,
    limit: PAGE_LIMIT, offset,
  });

  return (
    <div data-testid="account-list-page" className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">
            Accounts
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-text-secondary">
            Track employer Accounts, renewals, contacts, and open
            Commitments.
          </p>
        </div>
        <Link
          to="/dashboard/accounts/new"
          data-testid="account-list-new-button"
          className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse transition-fast ease-out hover:opacity-90"
        >
          + New Account
        </Link>
      </header>

      <AccountListFilters
        filters={filters}
        onChange={(next) => updateUrl(next, sortBy, sortDir, 0)}
        onClear={() => updateUrl({}, sortBy, sortDir, 0)}
      />

      {query.isLoading ? (
        <DashboardLoadingState variant="table" rows={6} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Accounts"
          description="Refresh to retry, or check the network connection."
          retry={() => query.refetch()}
        />
      ) : query.data && query.data.rows.length > 0 ? (
        <AccountListTable
          rows={query.data.rows}
          total={query.data.total}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={({ sortBy: nextBy, sortDir: nextDir }) =>
            updateUrl(filters, nextBy, nextDir, 0)
          }
          limit={PAGE_LIMIT}
          offset={offset}
          onOffsetChange={(next) => updateUrl(filters, sortBy, sortDir, next)}
        />
      ) : (
        <EmptyState
          hasActiveFilters={hasActiveFilters}
          onClear={() => updateUrl({}, sortBy, sortDir, 0)}
        />
      )}
    </div>
  );
}


function EmptyState({
  hasActiveFilters, onClear,
}: { hasActiveFilters: boolean; onClear: () => void }) {
  if (hasActiveFilters) {
    return (
      <DashboardEmptyState
        title="No Accounts match these filters"
        description="Clear the filters to see every Account in your brokerage."
        action={
          <button
            type="button"
            data-testid="account-list-empty-clear"
            onClick={onClear}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            Clear filters
          </button>
        }
      />
    );
  }
  return (
    <DashboardEmptyState
      title="No Accounts yet"
      description="Once you create an Account, it appears here."
    />
  );
}
